import React, { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
// ** MUI Imports
import Spinner from "src/@core/components/spinner";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import AlertCircleOutline from "mdi-material-ui/AlertCircleOutline";
import { course_get, course_update, ping_post } from "src/api/courses";
import CoursePlayerLayout from "../../layouts/CourseViewLayout/CoursePlayerLayout";
import { useSettings } from "src/@core/hooks/useSettings";
import { isNilOrEmpty } from "@salespark/toolkit";
// ** Third Party Components
import toast from "react-hot-toast";

// ** Hooks
import { useAuth } from "src/hooks/useAuth";
import { pathWithPreservedSearch, searchSuffixWithoutToken } from "src/@core/utils/urlQueryPreservation";

const Course = ({ match }) => {
  const auth = useAuth();
  const { settings, saveSettings } = useSettings();
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState({});
  const [actualChapter, setActualChapter] = useState(null);
  const [actualLesson, setActualLesson] = useState(null);
  const [isFirstLesson, setIsFirstLesson] = useState(true);
  const [isLastLesson, setIsLastLesson] = useState(false);
  const [lessonsArray, setLessonsArray] = useState([]);

  const [lessonsStatus, setLessonsStatus] = useState([]);
  const [errorLoadingData, setErrorLoadingData] = useState({ visible: false, message: "", code: -1 });
  const [noLessonsAvaliable, setNoLessonsAvaliable] = useState(false);
  const [videosInfos, setVideosInfos] = useState([]);

  let params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const course_id = params?.course_id;
  const chapter_id = params?.chapter_id;
  const lesson_id = params?.lesson_id;

  const authUser = auth?.user;
  const pingContextRef = useRef({ authUser: null, course_id: null, chapter: null, lesson: null });

  useEffect(() => {
    if (course_id && authUser) loadCourse(course_id);
  }, [course_id, authUser]);

  useEffect(() => {
    pingContextRef.current = {
      authUser,
      course_id,
      chapter: actualChapter,
      lesson: actualLesson,
    };
  }, [authUser, course_id, actualChapter, actualLesson]);

  const ping = async () => {
    try {
      const { authUser: currentUser, course_id: currentCourseId, chapter, lesson } = pingContextRef.current;
      if (isNilOrEmpty(currentUser) || isNilOrEmpty(currentCourseId)) return;
      if (isNilOrEmpty(chapter) || isNilOrEmpty(lesson)) return;
      const response = await ping_post(currentUser, currentCourseId, { chapter, lesson });
      if (response.status) {
        console.log("I'm alive and kicking!");
      }
    } catch (error) {
      console.error("Ping failed", error);
    }
  };

  useEffect(() => {
    ping();
    const intervalId = setInterval(() => {
      ping();
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const loadCourse = async (course_id) => {
    try {
      setErrorLoadingData({ visible: false, message: "", code: -1 });
      setIsLoading(true);

      const response = await dbGetCourse(authUser, course_id);
      if (!response.status) {
        if (response.data?.response?.data?.code === 6624) {
          setErrorLoadingData({
            visible: true,
            message: "Já terminou o tempo de acesso a este curso.",
            code: 6624,
          });
        } else if (response.data?.response?.data?.code === 6623) {
          setErrorLoadingData({
            visible: true,
            message: (
              <>
                Não tens acesso a este curso, o curso não existe ou não se encontra disponível de momento.
                <br />
                <br />
                Caso o erro persista verifica junto do produtor se o curso foi temporáriamente pausado para efeitos de atualização ou o teu acesso removido.
              </>
            ),
            code: 6623,
          });
        } else {
          const message = response.data?.response?.data?.message || "Não foi possível carregar o curso. Tenta novamente mais tarde.";

          setErrorLoadingData({
            visible: true,
            message: message,
            code: -1,
          });
        }
        setIsLoading(false);
        return;
      }
      const { course, details, videos_info } = response.data;

      if (details.length === 0) {
        setIsLoading(false);
        setNoLessonsAvaliable(true);
        return;
      }

      const _actualChapter = chapter_id || course.actual_chapter || details[0].chapter_id;
      const _actualLesson = lesson_id || course.actual_lesson || details[0].lessons[0].lesson_id;

      const courseData = response.data;

      // count lessons
      //  const lessonsCount = courseData.details.reduce((acc, chapter) => acc + chapter.lessons.length, 0);
      const _lessonsArray = courseData.details.map((chapter) =>
        chapter.lessons.map((lesson) => {
          return { chapter: chapter.chapter_id, lesson: lesson.lesson_id };
        }),
      );

      //Flatten lessonsArray
      const __lessonsArray = [].concat.apply([], _lessonsArray);

      setLessonsArray(__lessonsArray);
      updateFirstLastLesson(__lessonsArray, _actualLesson);

      setCourse(response.data);
      setVideosInfos(videos_info);
      setLessonsStatus(course.lessonStatus || []);

      dbUpdateCourse(auth.user, course_id, "global", {
        started: true,
        actual_chapter: _actualChapter,
        actual_lesson: _actualLesson,
      });
      setActiveLesson(_actualChapter, _actualLesson);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setErrorLoadingData({
        visible: true,
        message: "Não foi possível carregar o curso. Tenta novamente mais tarde.",
        code: -1,
      });
    }
  };

  const updateFirstLastLesson = (lessons, lesson) => {
    // console.log("lessonsArray", lessons);
    try {
      if (lessons.length > 0) {
        // Normalize IDs before comparing: on initial page load `lesson` comes from the URL
        // params as a string, while lesson IDs in the course data are numbers. A strict ===
        // would fail (e.g. 5040318689 === "5040318689"), leaving the last/first flags wrong
        // until the user navigated away and back. parseInt both sides so the check is correct
        // on page open too.
        const _lesson = parseInt(lesson);
        const _isFirstLesson = parseInt(lessons[0].lesson) === _lesson;
        const _isLastLesson = parseInt(lessons[lessons.length - 1].lesson) === _lesson;
        //  console.log("isFirstLesson", _isFirstLesson);
        //  console.log("isLastLesson", _isLastLesson);
        setIsFirstLesson(_isFirstLesson);
        setIsLastLesson(_isLastLesson);
      }
    } catch (error) {
      // console.log("updateFirstLastLesson error", error);
    }
  };

  const setActiveLesson = (chapter, lesson) => {
    setActualChapter(parseInt(chapter));
    setActualLesson(parseInt(lesson));
    updateFirstLastLesson(lessonsArray, lesson);
    navigate(`/courses/${course_id}/${chapter}/${lesson}${searchSuffixWithoutToken(location.search)}`, { replace: true });
  };

  const dbGetCourse = (auth_data, course_id) => {
    return new Promise((resolve) => {
      try {
        course_get(auth_data, course_id)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  /** Sync playlist completion from API after questionnaire submit (server updates lessonStatus on POST). */
  const refreshLessonsStatus = async () => {
    const response = await dbGetCourse(authUser, course_id);
    if (response.status !== true) {
      return;
    }
    const innerCourse = response.data?.course;
    if (innerCourse?.lessonStatus != null) {
      setLessonsStatus(innerCourse.lessonStatus || []);
    }
  };

  const dbUpdateCourse = (auth_data, course_id, action, payload) => {
    return new Promise((resolve) => {
      try {
        course_update(auth_data, course_id, action, payload)
          .then((res) => resolve({ status: true, data: res.data }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const closeError = () => {
    setErrorLoadingData({ visible: false, message: "", code: -1 });

    navigate(pathWithPreservedSearch("/home", location.search, { type: "error" }));
  };

  const findLessonMetaById = (lesson_id_num) => {
    const chapters = course?.details;
    if (Array.isArray(chapters) === false) {
      return null;
    }
    for (const ch of chapters) {
      const hit = ch.lessons?.find((l) => l.lesson_id === lesson_id_num);
      if (hit) {
        return hit;
      }
    }
    return null;
  };

  const lessonStatusHandler = async (chapter_id, lesson_id, completed, meta = {}) => {
    const lessonMeta = findLessonMetaById(lesson_id);
    if (lessonMeta?.lesson_type === "questionnaire") {
      if (completed !== true || meta?.fromQuestionnaireSubmit !== true) {
        return;
      }
    }

    let previousStatus = [];

    setLessonsStatus((prev) => {
      previousStatus = prev;
      const filtered = prev.filter((status) => status.lesson_id !== lesson_id);
      return [...filtered, { lesson_id, completed }];
    });

    const response = await dbUpdateCourse(auth.user, course_id, "updateLessonStatus", {
      lesson_id,
      completed,
    });
    if (!response.status) {
      // const selectedColor = theme.palette.mode === 'dark' ? '#a35a5a' : '#ffe3e3'

      toast.error("Oops... Erro ao atualizar estado da aula...", {
        position: "top-right",
      });

      setLessonsStatus(previousStatus);
      return;
    }
    setLessonsStatus(response.data.lessonStatus);
    // console.log("error", response.data);
  };

  const activeLessonHandler = (chapter, lesson_id) => {
    setActiveLesson(chapter, lesson_id);
    updateFirstLastLesson(lessonsArray, lesson_id);
    dbUpdateCourse(auth.user, course_id, "global", { actual_chapter: chapter, actual_lesson: lesson_id });
  };

  const updateVideoCurrentTime = (chapter, lesson_id, video_id, current_time) => {
    // console.log("updateVideoCurrentTime", chapter, lesson_id, video_id, current_time);
    if (!video_id || !current_time) return;
    dbUpdateCourse(auth.user, course_id, "updateVideoCurrentTime", { video_id, current_time });
  };

  const updateAudioCurrentTime = (lesson_id, current_time) => {
    if (!lesson_id || !current_time) return;
    dbUpdateCourse(auth.user, course_id, "updateAudioCurrentTime", { lesson_id, current_time });
  };

  const moveToPreviousLesson = () => {
    course.details.forEach((chapter) => {
      chapter.lessons.forEach((lesson) => {
        if (lesson.lesson_id === actualLesson) {
          const nextLesson = chapter.lessons[chapter.lessons.indexOf(lesson) - 1];
          if (nextLesson) {
            // setActualLessonIndex(counter);
            activeLessonHandler(chapter.chapter_id, nextLesson.lesson_id);
          } else {
            const nextChapter = course.details[course.details.indexOf(chapter) - 1];
            if (nextChapter) {
              //  setActualLessonIndex(counter);
              activeLessonHandler(nextChapter.chapter_id, nextChapter.lessons[nextChapter.lessons.length - 1].lesson_id);
            }
          }
        }
      });
    });
  };
  const moveToNextLesson = () => {
    course.details.forEach((chapter) => {
      chapter.lessons.forEach((lesson) => {
        if (lesson.lesson_id === actualLesson) {
          const nextLesson = chapter.lessons[chapter.lessons.indexOf(lesson) + 1];
          if (nextLesson) {
            //setActualLessonIndex(counter);
            activeLessonHandler(chapter.chapter_id, nextLesson.lesson_id);
          } else {
            const nextChapter = course.details[course.details.indexOf(chapter) + 1];
            if (nextChapter) {
              // setActualLessonIndex(counter);
              activeLessonHandler(nextChapter.chapter_id, nextChapter.lessons[0].lesson_id);
            }
          }
        }
      });
    });
  };

  return (
    <>
      {/* <MobileView>
        <Alert severity="warning">Ainda não existe suporte completo a telemóveis. Brevemente.</Alert>
      </MobileView> */}
      {!isLoading && !errorLoadingData.visible && !noLessonsAvaliable && (
        <CoursePlayerLayout
          // lessonsArray={lessonsArray}
          isFirstLesson={isFirstLesson}
          isLastLesson={isLastLesson}
          // actualLessonIndex={actualLessonIndex}
          moveToPreviousLesson={moveToPreviousLesson}
          moveToNextLesson={moveToNextLesson}
          updateVideoCurrentTime={updateVideoCurrentTime}
          updateAudioCurrentTime={updateAudioCurrentTime}
          settings={settings}
          saveSettings={saveSettings}
          data={course}
          videos_info={videosInfos}
          actualChapter={actualChapter}
          actualLesson={actualLesson}
          lessonsStatus={lessonsStatus}
          lessonStatusHandler={lessonStatusHandler}
          activeLessonHandler={activeLessonHandler}
          refreshLessonsStatus={refreshLessonsStatus}
        />
      )}
      {isLoading && <Spinner title="A carregar curso..." />}
      {noLessonsAvaliable && (
        <Fragment>
          <Dialog
            open={noLessonsAvaliable}
            onClose={() => navigate(pathWithPreservedSearch("/home", location.search))}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ className: "sp-modal__paper" }}
          >
            <DialogContent className="sp-modal">
              <div className="sp-modal__head">
                <span className="sp-modal__icon">
                  <AlertCircleOutline />
                </span>
                <h2 id="alert-dialog-title" className="sp-modal__title">
                  Oops!
                </h2>
              </div>
              <div id="alert-dialog-description" className="sp-modal__body">
                Não existem aulas disponíveis para este curso. Em caso de dúvidas, contacta o produtor deste produto.
              </div>
            </DialogContent>
            <DialogActions className="sp-modal__actions">
              <button
                type="button"
                className="sp-modal__btn sp-modal__btn--primary"
                onClick={() => navigate(pathWithPreservedSearch("/home", location.search))}
              >
                Voltar
              </button>
            </DialogActions>
          </Dialog>
        </Fragment>
      )}
      {errorLoadingData.visible && (
        <Fragment>
          <Dialog
            open={errorLoadingData.visible}
            onClose={closeError}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ className: "sp-modal__paper" }}
          >
            <DialogContent className="sp-modal sp-modal--left">
              {errorLoadingData.code === 6623 && <></>}
              <div className="sp-modal__head">
                <span className="sp-modal__icon">
                  <AlertCircleOutline />
                </span>
                <h2 id="alert-dialog-title" className="sp-modal__title">
                  Oops!
                </h2>
              </div>
              <div id="alert-dialog-description" className="sp-modal__body">
                {errorLoadingData.message}
              </div>
            </DialogContent>
            <DialogActions className="sp-modal__actions">
              <button type="button" className="sp-modal__btn sp-modal__btn--ghost" onClick={closeError}>
                Voltar
              </button>
              <button type="button" className="sp-modal__btn sp-modal__btn--primary" onClick={() => loadCourse(course_id)}>
                Tentar novamente
              </button>
            </DialogActions>
          </Dialog>
        </Fragment>
      )}
    </>
  );
};

export default Course;
